import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import { Button, MenuItem, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFindById } from '../../../services/FormQueryService';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { RowFlexLayout } from '../../layout';

import {
    CERTIFICATE_NAME_FIELD,
    CERTIFICATE_USAGE_FIELD,
    CERTIFICATE_FIELD,
    CERTIFICATE_DESCRIPTION_FIELD,
    CERTIFICATE_USAGES,
    CERTIFICATE_ASSOCIATION_FIELD,
    PRIVATE_KEY_PASS_FIELD,
    ASSOCIATION_TYPE,
    TAB_TRUSTED,
    PRIVATE_KEY_VALUE_FIELD,
} from './CertificateConstants';
import { getUrlParamFromState } from '../../../utilities/PageStateUrlUtilities';
import NgpTextField from '../../form/NgpTextField';
import NgpTextarea from '../../form/NgpTextarea';
import NgpSelect from '../../form/NgpSelect';
import NgpPasswordField from '../../form/NgpPasswordField';
import NgpFileUpload from '../../form/NgpFileUpload';
import { AdminPages, buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import { errorAlertObject } from '../../form/FormConstants';
import { HTTP_STATUS_400 } from '../../../services/ApiDataService';
import CertificateAgents from './CertificateAgents';
import useUiAlerts, { createAlertConfig, ERROR } from '../../../stores/AlertsStore';
import { useDetailStyles } from './CertificateStyles';

const MAX_FILE_SIZE_ALLOWED = 2.202e6; // 2MB
const functionName = AdminPages.AgentCertificates;
const defaultValues = {
    [CERTIFICATE_USAGE_FIELD]: CERTIFICATE_USAGES.wifi,
    [CERTIFICATE_ASSOCIATION_FIELD]: ASSOCIATION_TYPE.shared,
};

function isValidPrivateKey(value) {
    if (!value) return true;

    const keyFmt =
        /(-+BEGIN (ANY |RSA |DSA |ENCRYPTED |EC |)PRIVATE KEY-+)(.*?)(-+END (ANY |RSA |DSA |ENCRYPTED |EC |)PRIVATE KEY-+)/s;
    return keyFmt.test(value) ? true : 'Invalid private key format';
}

function isValidCertificate(value) {
    if (!value) return true;

    const certFmt =
        /(-+BEGIN (X509 CERTIFICATE|CERTIFICATE|CERTIFICATE PAIR|TRUSTED CERTIFICATE)-+)(.*?)(-+END (X509 CERTIFICATE|CERTIFICATE|CERTIFICATE PAIR|TRUSTED CERTIFICATE)-+)/s;
    return certFmt.test(value) ? true : 'Invalid certificate format';
}

const CERTIFICATE_UPLOAD_RULES = {
    validate: {
        certificate: isValidCertificate,
    },
};

const PRIVATE_KEY_UPLOAD_RULES = {
    validate: {
        privateKey: isValidPrivateKey,
    },
};

function prepareSavedData(dataToSave, formData) {
    const putData = _.merge({}, formData, dataToSave);
    return putData;
}

export default function CertificateDetailPage(props) {
    const { activeTab, dataSource } = props;
    const goToUrl = `/admin/certificate?${getUrlParamFromState({ activeTab })}`;
    const baseUrl = dataSource.crudUrl;
    const recordId = useEntityIdParam();
    const formDeleteService = useFormDeleteService();
    const formSaveService = useFormSaveService();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const { reset, setError, handleSubmit, watch, getValues } = methods;
    const privateKeyValue = watch(PRIVATE_KEY_VALUE_FIELD);
    const certificateValue = watch(CERTIFICATE_FIELD);
    const certificateUsage = watch(CERTIFICATE_USAGE_FIELD);
    const certType = watch(CERTIFICATE_ASSOCIATION_FIELD);
    const functionBreadCrumbs = [
        { link: goToUrl, text: activeTab === TAB_TRUSTED ? t`Trusted Certificates` : t`Certificates` },
    ];
    const newBreadCrumbText = t`New`;
    const [, uiAlertActions] = useUiAlerts();
    const classes = useDetailStyles();

    const [agentSelectionIsDirty, setAgentSelectionIsDirty] = useState(false);
    const [showDeleteButton, setShowDeleteButton] = useState(false);

    const needShowAgents = useMemo(() => {
        if (certificateUsage === CERTIFICATE_USAGES.voip) return true;
        return certType === ASSOCIATION_TYPE.device && certificateUsage !== CERTIFICATE_USAGES.web;
    }, [certificateUsage, certType]);

    /**
     * Post handling of data coming back from the server
     * @param response
     * @returns {*}
     */
    const postFindByIdHandler = (response) => {
        setShowDeleteButton(true);
        return response;
    };

    const { initialized, finding, formData } = useFindById({
        url: baseUrl,
        recordId,
        postFindByIdHandler,
        resetFunc: reset,
        defaultValues,
        errorHandler: errorWrapper({}, goToUrl),
    });
    const breadcrumbs = useBreadcrumb(
        functionBreadCrumbs,
        recordId,
        formData,
        CERTIFICATE_NAME_FIELD,
        newBreadCrumbText
    );

    const handleFormSubmit = (dataToSave = {}) => {
        const putData = prepareSavedData(dataToSave, formData);

        if (activeTab !== TAB_TRUSTED) {
            _.set(putData, 'nPoints.ids', needShowAgents ? getValues('nPoints.ids') || [] : []);
        }

        return formSaveService.save({
            url: baseUrl,
            recordId: formData._id,
            dataToSave: putData,
            setError,
        });
    };

    const deleteErrorHandler = (errorObject) => {
        uiAlertActions.createAlert(
            errorObject.getStatus && errorObject.getStatus() === HTTP_STATUS_400
                ? createAlertConfig(ERROR, _.get(errorObject.responseBody || errorObject, 'message'))
                : errorAlertObject
        );
    };

    const handleConfirmedDelete = () => {
        return deleteWrapper({
            deleteFunction: () => formDeleteService.deleteById(baseUrl, recordId),
            onDeleteGoTo: goToUrl,
            errorHandler: deleteErrorHandler,
        })();
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit, goToUrl);

    const dirtyCheck = (isDirty) => {
        return isDirty || agentSelectionIsDirty;
    };
    const rules = {
        required: false,
        validate: {
            format: (value) => {
                if (value.length < 8 && value.length) {
                    return 'Minimum of 8 characters is required.';
                }
                return true;
            },
        },
    };

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    {showDeleteButton && (
                        <ConfirmDeleteButton handleConfirmedDelete={handleConfirmedDelete} plural={false} />
                    )}
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized}
                    loading={finding || saving || deleting}
                    isDirtyFunc={dirtyCheck}
                >
                    <FormSection title={<Trans>Certificate Details</Trans>}>
                        <NgpTextField
                            fieldName={CERTIFICATE_NAME_FIELD}
                            label={<Trans>Name</Trans>}
                            required
                            inputProps={{ maxLength: 128, size: 32 }}
                        />

                        <NgpTextarea
                            fieldName={CERTIFICATE_DESCRIPTION_FIELD}
                            label={<Trans>Description</Trans>}
                            cols={90}
                            inputProps={{ maxLength: 225 }}
                        />
                        {activeTab !== TAB_TRUSTED && (
                            <>
                                <NgpSelect
                                    fieldName={CERTIFICATE_USAGE_FIELD}
                                    label={<Trans>Certificate Usage</Trans>}
                                    disabled={!!recordId}
                                    className={classes.usageSelect}
                                >
                                    {_.map(CERTIFICATE_USAGES, (value, key) => (
                                        <MenuItem key={key} value={value}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </NgpSelect>
                                {[CERTIFICATE_USAGES.wifi, CERTIFICATE_USAGES.wired].includes(certificateUsage) && (
                                    <NgpSelect
                                        disabled={!!recordId}
                                        fieldName={CERTIFICATE_ASSOCIATION_FIELD}
                                        label={<Trans>Association Type</Trans>}
                                        className={classes.usageSelect}
                                    >
                                        {_.map(ASSOCIATION_TYPE, (value, key) => (
                                            <MenuItem key={key} value={value}>
                                                {value}
                                            </MenuItem>
                                        ))}
                                    </NgpSelect>
                                )}
                            </>
                        )}

                        <RowFlexLayout vStart mt={4}>
                            <NgpFileUpload
                                fieldName={CERTIFICATE_FIELD}
                                label={t`UPLOAD CERTIFICATE`}
                                maxFileSize={MAX_FILE_SIZE_ALLOWED}
                                showFileName={false}
                                buttonProps={{ color: 'primary', variant: 'text', size: 'medium' }}
                                required
                                rules={CERTIFICATE_UPLOAD_RULES}
                            />
                            {certificateValue ? (
                                <Typography
                                    variant="body2"
                                    component="div"
                                    className={`${classes.success} ${classes.uploadStatus}`}
                                >
                                    <CheckCircleIcon />
                                    {t`Certificate present`}
                                </Typography>
                            ) : (
                                <Typography
                                    variant="body2"
                                    component="div"
                                    className={`${classes.failure} ${classes.uploadStatus}`}
                                >
                                    <CancelIcon className="upload-missing" />
                                    {t`No PEM Certificate selected`}
                                </Typography>
                            )}
                        </RowFlexLayout>
                        {activeTab !== TAB_TRUSTED && (
                            <>
                                <RowFlexLayout vStart mt={2}>
                                    <NgpFileUpload
                                        fieldName={PRIVATE_KEY_VALUE_FIELD}
                                        label={t`UPLOAD PRIVATE KEY`}
                                        maxFileSize={MAX_FILE_SIZE_ALLOWED}
                                        showFileName={false}
                                        buttonProps={{ color: 'primary', variant: 'text', size: 'medium' }}
                                        required
                                        rules={PRIVATE_KEY_UPLOAD_RULES}
                                    />
                                    {privateKeyValue ? (
                                        <Typography
                                            variant="body2"
                                            component="div"
                                            className={`${classes.success} ${classes.uploadStatus}`}
                                        >
                                            <CheckCircleIcon />
                                            {t`Private Key present`}
                                        </Typography>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            component="div"
                                            className={`${classes.failure} ${classes.uploadStatus}`}
                                        >
                                            <CancelIcon className="upload-missing" />
                                            {t`No PEM Private Key selected`}
                                        </Typography>
                                    )}
                                </RowFlexLayout>

                                <NgpPasswordField
                                    fieldName={PRIVATE_KEY_PASS_FIELD}
                                    fieldRules={rules}
                                    label={<Trans>Private Key Passphrase</Trans>}
                                    inputProps={{ size: 65, maxLength: 128, minWidth: 8 }}
                                    togglePasswordVisibility
                                />
                                <div className={classes.helpText}>
                                    Private keys uploaded without a passphrase will automatically be assigned a random
                                    passphrase
                                </div>
                            </>
                        )}
                    </FormSection>
                    {activeTab !== TAB_TRUSTED && needShowAgents && (
                        <CertificateAgents
                            key={certificateUsage}
                            formData={formData}
                            setAgentSelectionIsDirty={setAgentSelectionIsDirty}
                            certificateUsage={certificateUsage}
                        />
                    )}
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
